<template>
  <UIPanel
    v-model="clearedSearch"
    v-model:storage="searchStorage"
    unshrinkable
    placeholder="Search transactions..."
  />
  <UIGridSkeleton
    v-if="disabled"
    :columns
    :sm
    message="Loading transactions..."
  />
  <UIGrid v-else v-model:sort="sort" :columns :items :sm scrollable />
  <UIPagination
    :disabled
    :page-no
    :page-size="pagination?.page_size"
    :total="pagination?.total"
    class="asset-drawer__pagination"
    @click:page="handleClickPage"
  />
</template>

<script setup lang="ts">
import { computed, onWatcherCleanup, ref, watch } from 'vue'
import { isEqual } from 'lodash'
import { debouncedWatch } from '@vueuse/core'

import {
  Transaction,
  PaginatedMeta,
  Sort,
  FilterComparison,
  FilterLogic,
} from '@types'

import {
  ASSET_TRANSACTIONS_PAGE_SIZE,
  DEBOUNCE_DELAY,
} from '@/const/common'
import {
  TRANSACTION_ENTRY_FIELD,
  TRANSACTION_ENTRY_FIELD_LABEL,
  TRANSACTION_FIELD,
  TRANSACTION_FIELD_LABEL,
} from '@/const'

import { handleCatchedError } from '@/helpers/common'
import { amountTooltip, displayAmount } from '@/entities/transactions/utils/helpers'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useTransactionsStore } from '@/store/transactions'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'

import { UIPagination, UIPanel } from '@ui/core'
import { UIGrid, UIGridSkeleton } from '@ui/grid'

defineOptions({ inheritAttrs: false })

const assetsBunchStore = useAssetsBunchStore()
const transactionsStore = useTransactionsStore()
const transactionsSettingsStore = useTransactionsSettingsStore()

const items = ref<Transaction[]>([])
const pagination = ref<PaginatedMeta>()

const searchStorage = ref<string>()
const clearedSearch = ref<string>()
const sort = ref<Sort[]>()
const pageNo = ref(0)
const isLoading = ref(false)

const disabled = computed(
  () => isLoading.value || !transactionsSettingsStore.initFlag,
)

const settingsList = computed(() => transactionsSettingsStore.getList)

const sm = '1fr'
const columns = computed(() => [
  {
    name: TRANSACTION_FIELD.DATE,
    caption: TRANSACTION_FIELD_LABEL[TRANSACTION_FIELD.DATE],
    filter: {
      isDate: true,
    },
  },
  {
    name: 'entries.0.account_id',
    caption: TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.ACCOUNT_ID],
    formatter: (id: string) => assetsBunchStore.getNameById(id),
  },
  {
    name: 'entries.0.amount',
    caption: TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.AMOUNT],
    cellValueClasses: 'ui-grid__cell--right number-node',
    formatter: amount1Formatter,
    tooltip: amountTooltipHandler,
  },
  {
    name: 'entries.0.asset_id',
    caption: TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.ASSET_ID],
    formatter: (id: string) => assetsBunchStore.getNameById(id),
  },
  {
    name: TRANSACTION_FIELD.DESCRIPTION,
    caption: TRANSACTION_FIELD_LABEL[TRANSACTION_FIELD.DESCRIPTION],
  },
])

const fetchOptions = computed(() => ({
  search: clearedSearch.value,
  sort: sort.value?.map((item, index) => ({ ...item, index })),
  filter: {
    params: [
      {
        field: 'type',
        comparison: FilterComparison.EQ,
        value: 'add account',
      },
    ],
    logic: FilterLogic.AND,
  },
}))

const amountFormatter = (value: number | null | undefined, data: Transaction, index: number) => {
  const settings = settingsList.value.find(item => item.name === data.type)?.entries[index]?.amount
  if (!settings) return ''
  const isPercent = settings?.rule === 'percent'
  return displayAmount(value, isPercent)
}

const amount1Formatter = (value: number | null | undefined, data: Transaction) => {
  return amountFormatter(value, data, 0)
}

const amountTooltipHandler = (field: string, data: Transaction) => {
  if (!data) return
  const fieldIndex = Number(field.replace(/^entries\.(\d+?)\.amount/, '$1'))
  const settings = settingsList.value.find(item => item.name === data.type)
  const value = data.entries[fieldIndex]?.amount
  if (value === null || value === undefined) return
  const isPercent = settings?.entries[fieldIndex]?.amount?.rule === 'percent'
  return amountTooltip(value, isPercent)
}

const fetch = async (signal?: AbortSignal) => {
  try {
    isLoading.value = true
    const response = await transactionsStore.fetch(
      {
        ...fetchOptions.value,
        page_no: pageNo.value,
        page_size: ASSET_TRANSACTIONS_PAGE_SIZE,
      },
      false,
      signal,
      false,
    )
    items.value = response.data
    pagination.value = response.meta
  } catch (e) {
    handleCatchedError(e as string, fetchOptions.value)
  } finally {
    isLoading.value = false
  }
}

const handleClickPage = (page: number) => {
  pageNo.value = page
}

watch(pageNo, () => {
  const controller = new AbortController()
  fetch(controller.signal)
  onWatcherCleanup(() => {
    controller.abort()
  })
}, { immediate: true })

debouncedWatch(
  fetchOptions,
  (value, prev) => {
    if (isEqual(value, prev)) return
    fetch()
  },
  {
    deep: true,
    debounce: DEBOUNCE_DELAY,
  },
)
</script>
